import Vue from 'vue'

const state = {
  session: null,
  isStarting: false,
  pollingInterval: null,
  maxPollingAttempts: 120, // 10 minutes with 5-second intervals
  currentPollingAttempts: 0,
  showInlineVerification: false,
  error: null,
  isPolling: false
}

const getters = {
  fincheckerSession: state => state.session,
  isStartingVerification: state => state.isStarting,
  showInlineVerification: state => state.showInlineVerification,
  verificationError: state => state.error,
  isPolling: state => state.isPolling,
  hasActiveSession: state => state.session !== null,
  isPendingVerification: state => state.session && state.session.status === 'pending',
  isVerificationComplete: state => state.session && (state.session.status === 'success' || state.session.status === 'fail'),
  sessionStatus: state => state.session ? state.session.status : null
}

const mutations = {
  SET_SESSION(state, session) {
    state.session = session
  },
  
  SET_IS_STARTING(state, isStarting) {
    state.isStarting = isStarting
  },
  
  SET_POLLING_INTERVAL(state, interval) {
    state.pollingInterval = interval
  },
  
  SET_POLLING_ATTEMPTS(state, attempts) {
    state.currentPollingAttempts = attempts
  },
  
  INCREMENT_POLLING_ATTEMPTS(state) {
    state.currentPollingAttempts++
  },
  
  SET_SHOW_INLINE_VERIFICATION(state, show) {
    state.showInlineVerification = show
  },
  
  SET_ERROR(state, error) {
    state.error = error
  },
  
  SET_IS_POLLING(state, isPolling) {
    state.isPolling = isPolling
  },
  
  UPDATE_SESSION_STATUS(state, status) {
    if (state.session) {
      state.session.status = status
    }
  },
  
  CLEAR_SESSION(state) {
    state.session = null
    state.error = null
    state.showInlineVerification = false
  },
  
  RESET_POLLING_STATE(state) {
    state.currentPollingAttempts = 0
    state.isPolling = false
    if (state.pollingInterval) {
      clearInterval(state.pollingInterval)
      state.pollingInterval = null
    }
  }
}

const actions = {
  async startVerification({ commit, dispatch }) {
    commit('SET_IS_STARTING', true)
    commit('SET_ERROR', null)
    
    try {
      const response = await Vue.prototype.$http.get('finchecker/enrolment')
      // Handle API response structure
      if (response.data && response.data.status === 'OK' && response.data.payload) {
        const { session_id, enrolment_url } = response.data.payload
        
        const session = {
          sessionId: session_id,
          status: 'pending',
          enrolmentUrl: enrolment_url
        }
        
        commit('SET_SESSION', session)
        
        // Open finchecker in new tab
        window.open(enrolment_url, '_blank')
        
        // Start polling
        dispatch('startPolling', session_id)
        
        return { success: true, session }
      } else if (response.data && response.data.status === 'ERROR') {
        const errorMessage = response.data.message || 'Something went wrong!'
        commit('SET_ERROR', errorMessage)
        return { success: false, error: errorMessage }
      } else {
        throw new Error('Invalid response from server')
      }
    } catch (error) {
      console.error('Error starting finchecker verification:', error)
      const errorMessage = error.response?.data?.message || 'Failed to start verification. Please try again.'
      commit('SET_ERROR', errorMessage)
      
      if (error.response?.status === 401) {
        // Handle authentication error - this should be handled by the component
        return { success: false, error: errorMessage, needsAuth: true }
      }
      
      return { success: false, error: errorMessage }
    } finally {
      commit('SET_IS_STARTING', false)
    }
  },
  
  startPolling({ commit, dispatch }, sessionId) {
    commit('SET_POLLING_ATTEMPTS', 0)
    commit('SET_IS_POLLING', true)
    
    const interval = setInterval(() => {
      dispatch('checkSessionStatus', sessionId)
    }, 5000) // Poll every 5 seconds
    
    commit('SET_POLLING_INTERVAL', interval)
  },
  
  async checkSessionStatus({ commit, dispatch, state }, sessionId) {
    commit('INCREMENT_POLLING_ATTEMPTS')
    
    try {
      const response = await Vue.prototype.$http.get(`finchecker/check/${sessionId}`)
      
      if (response.data && response.data.status) {
        commit('UPDATE_SESSION_STATUS', response.data.status)
        
        // Stop polling if verification is complete
        if (response.data.status === 'success' || response.data.status === 'fail') {
          dispatch('stopPolling')
          
          // Dispatch event for components to handle completion
          return { completed: true, status: response.data.status }
        }
      }
    } catch (error) {
      console.error('Error checking finchecker status:', error)
      
      if (error.response?.status === 401) {
        dispatch('stopPolling')
        return { error: 'Authentication required', needsAuth: true }
      }
    }
    
    // Stop polling if max attempts reached
    if (state.currentPollingAttempts >= state.maxPollingAttempts) {
      dispatch('stopPolling')
      commit('SET_ERROR', 'Verification is taking longer than expected. Please refresh the page to check status.')
    }
    
    return { completed: false }
  },
  
  stopPolling({ commit, state }) {
    if (state.pollingInterval) {
      clearInterval(state.pollingInterval)
      commit('SET_POLLING_INTERVAL', null)
    }
    commit('SET_IS_POLLING', false)
  },
  
  reopenVerification({ state }) {
    if (state.session && state.session.enrolmentUrl) {
      window.open(state.session.enrolmentUrl, '_blank')
    }
  },
  
  toggleInlineVerification({ commit, state }) {
    commit('SET_SHOW_INLINE_VERIFICATION', !state.showInlineVerification)
  },
  
  clearError({ commit }) {
    commit('SET_ERROR', null)
  },
  
  resetSession({ commit, dispatch }) {
    dispatch('stopPolling')
    commit('CLEAR_SESSION')
    commit('RESET_POLLING_STATE')
  },
  
  // Check for existing session on app load
  async checkExistingSession({ commit, dispatch }) {
    try {
      // This would typically get the latest session from the backend
      const response = await Vue.prototype.$http.get('user/profile')
      
      // If user has a pending verification session, restore it
      if (response.data.verification_session) {
        const session = {
          sessionId: response.data.verification_session.session_id,
          status: response.data.verification_session.status,
          enrolmentUrl: response.data.verification_session.enrolment_url
        }
        
        commit('SET_SESSION', session)
        
        // If session is pending, start polling
        if (session.status === 'pending') {
          dispatch('startPolling', session.sessionId)
        }
        
        return session
      }
    } catch (error) {
      console.error('Error checking existing finchecker session:', error)
      // Don't show error to user as this is a background check
    }
    
    return null
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
